import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { AccountService } from '../../../../../../../services/account/account.service';
import { EventTypeService } from '../../../../../../../services/event-type/event-type.service';
import {
  eventPartIsEmpty,
  EventPricingMatrix,
  EventType,
  NonRepeatingEvent,
  NonRepeatingEventPart
} from '../../../../../../../models/event-type';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LocationArea } from '../../../../../../../models/location';
import { LocationService } from '../../../../../../../services/location/location.service';
import { QuickAddOccurrencesModalComponent } from '../../../event-setup/event-type-schedules/event-type-edit-schedule/event-type-edit-schedule-non-repeating/modals/quick-add-occurrences-modal/quick-add-occurrences-modal.component';
import { EventTypeStatus, ScheduleType } from '../../../../../../../models/event-type-enums';
import { TaxRate } from '../../../../../../../models/tax-rate';
import { TaxRateService } from '../../../../../../../services/tax-rate/tax-rate.service';
import { ScheduledBooking } from '../../../../../../../models/scheduled-booking';
import { ScheduledBookingService } from '../../../../../../../services/scheduled-booking/scheduled-booking.service';
import { dateStringFromISO8601, propExistsAndIsNonEmpty, timeStringFromISO8601 } from '../../../../../../../utils';
import { OrganizationConfigService } from '../../../../../../../services/organization-config/organization-config.service';
import { Membership } from '../../../../../../../models/membership';
import { MembershipPurchaseType, MembershipStatus } from '../../../../../../../models/membership-enums';
import { MembershipService } from '../../../../../../../services/membership/membership.service';


@Component({
  selector: 'app-private-event-edit-event',
  templateUrl: './private-event-edit-event.component.html',
  styleUrls: ['./private-event-edit-event.component.css']
})
export class PrivateEventEditEventComponent implements OnInit {

  readonly SUBMIT_TYPE_GO_BACK = 'goBack';
  readonly SUBMIT_TYPE_CONTINUE = 'continue';

  public isNewPrivateEvent: boolean = null;
  public requestBookingId: number = null;
  public privateEvent: NonRepeatingEvent;
  public submitType = this.SUBMIT_TYPE_CONTINUE;
  public showRequestBooking = false;

  public eventTypeList: EventType[] = null;
  public pricingMatrixList: EventPricingMatrix[] = null;
  public locationAreaList: LocationArea[];
  public taxRateList: TaxRate[];
  public benefitMembershipList: Membership[];
  public requestBooking: ScheduledBooking = null;

  public errorMessage = '';
  public errors: any = {};
  public successMessage = '';

  public loadingPrivateEvent = false;
  public loadingRequestBooking = false;
  public loadingEventTypes = false;
  public loadingLocationAreas = false;
  public loadingPricingMatrices = false;
  public loadingTaxRates = true;
  public loadingBenefitMemberships = true;
  public loadingSave = false;

  public loadPricingMatricesRequestCount = 0;

  public privateEventNotFound = false;
  public missingPermission = false;
  public serverError: number = null;

  public readonly dateStringFromISO8601 = dateStringFromISO8601;
  public readonly timeStringFromISO8601 = timeStringFromISO8601;
  public readonly Object = Object;
  public readonly propExistsAndIsNonEmpty = propExistsAndIsNonEmpty;

  constructor(
    public locationService: LocationService,
    public orgConfigService: OrganizationConfigService,
    private route: ActivatedRoute,
    private router: Router,
    private accountService: AccountService,
    private eventTypeService: EventTypeService,
    private scheduledBookingService: ScheduledBookingService,
    private taxRateService: TaxRateService,
    private membershipService: MembershipService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    const routeParams = this.route.snapshot.params;
    this.isNewPrivateEvent = !routeParams.hasOwnProperty('id');
    if (routeParams.hasOwnProperty('requestBookingId')) {
      this.requestBookingId = parseInt(routeParams.requestBookingId, 10);
      if (isNaN(this.requestBookingId)) { this.requestBookingId = null; }
    }

    if (!this.accountService.getMyAccount().can_manage_events) {
      this.missingPermission = true;
      return;
    }

    // Either get the event to edit (if an ID was provided) or construct an
    // empty event for creation.
    this.resetPrivateEvent();
    if (!this.isNewPrivateEvent) {
      this.loadPrivateEvent(routeParams.id);
    } else {
      if (this.requestBookingId) {
        this.showRequestBooking = true;
      } else {
        // If this is a new event, start it off with one event part.
        this.addNewEventPart();
      }
    }

    this.loadEventTypes();
    this.loadLocationAreas();
    this.loadTaxRates();
    this.loadBenefitMemberships();
    this.loadRequestBooking();
  }

  public loadLocationAreas() {
    this.loadingLocationAreas = true;

    this.serverError = null;

    this.locationService
      .getLocationAreas(
        1,
        100,
        {}
        )
      .then(response => { this.locationAreaList = response.results; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingLocationAreas = false; });
  }

  public loadPricingMatrices() {
    this.loadingPricingMatrices = true;

    this.loadPricingMatricesRequestCount++;
    const thisRequestCount = this.loadPricingMatricesRequestCount;

    if (!this.privateEvent.event_type_id) {
      this.pricingMatrixList = null;
      this.loadingPricingMatrices = false;
    }

    if (this.isNewPrivateEvent) {
      this.eventTypeService
        .getEventPricingMatrices(this.privateEvent.event_type_id, 1, 100)
        .then(response => {
          if (thisRequestCount !== this.loadPricingMatricesRequestCount) {
            return;
          }

          this.pricingMatrixList = response.results;

          if (this.pricingMatrixList.length > 0) {
            this.privateEvent.pricing_matrix_id = this.pricingMatrixList[0].id;
          } else {
            this.privateEvent.pricing_matrix_id = null;
          }
        })
        .catch(reason => {
          if (reason.status === 403) {
            this.accountService.logout();
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => {
          if (thisRequestCount === this.loadPricingMatricesRequestCount) {
            this.loadingPricingMatrices = false;
          }
        });
    } else if (this.privateEvent.pricing_matrix_id) {
      this.eventTypeService
        .getEventPricingMatrix(
          this.privateEvent.event_type_id,
          this.privateEvent.pricing_matrix_id
        )
        .then(response => {
          if (thisRequestCount !== this.loadPricingMatricesRequestCount) {
            return;
          }

          this.pricingMatrixList = [response];
        })
        .catch(reason => {
          if (reason.status === 403) {
            this.accountService.logout();
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => {
          if (thisRequestCount === this.loadPricingMatricesRequestCount) {
            this.loadingPricingMatrices = false;
          }
        });
    } else {
      this.pricingMatrixList = [];
      this.loadingPricingMatrices = false;
    }
  }

  public loadTaxRates() {
    this.loadingTaxRates = true;

    this.taxRateService
      .getTaxRates(1, 100)
      .then(response => {
        this.taxRateList = response.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingTaxRates = false; });
  }

  public benefitMembershipRetired(id: number | null): boolean {
    if (id === null || id === undefined) {
      return false;
    }
    return !this.benefitMembershipList ||
      !this.benefitMembershipList.some(membership => membership.id === id);
  }

  public loadBenefitMemberships() {
    this.loadingBenefitMemberships = true;

    this.membershipService
      .getMemberships(
        1,
        3000,
        {
          status: MembershipStatus.active,
          purchase_type: MembershipPurchaseType.benefit,
        }
      )
      .then(response => {
        this.benefitMembershipList = response.results;
      })
      .catch(reason => {
        this.serverError = reason.status;
      })
      .finally(() => { this.loadingBenefitMemberships = false; });
  }

  public getSelectedPricingMatrix() {
    if (!this.privateEvent.pricing_matrix_id) {
      return null;
    }

    for (const pricingMatrix of this.pricingMatrixList) {
      if (pricingMatrix.id === this.privateEvent.pricing_matrix_id) {
        return pricingMatrix;
      }
    }

    return null;
  }

  public addNewEventPart() {
    const newEventPart: NonRepeatingEventPart = {
      start_time: '',
      start_date: '',
      duration_minutes: null,
      instructors: [],
      start_datetime_isoformat: '',
      end_datetime_isoformat: ''
    };

    if (this.privateEvent.event_parts.length > 0) {
      const bottomEventPart = this.privateEvent.event_parts[this.privateEvent.event_parts.length - 1];
      newEventPart.start_time = bottomEventPart.start_time;
      newEventPart.duration_minutes = bottomEventPart.duration_minutes;
      for (const instructor of bottomEventPart.instructors) {
        // Create a deep copy of the instructor and add it to the new event part.
        newEventPart.instructors.push({
          id: instructor.id,
          first_name: instructor.first_name,
          last_name: instructor.last_name,
          is_sub: false
        });
      }
    }

    this.privateEvent.event_parts.push(newEventPart);
  }

  public save() {
    this.loadingSave = true;

    this.errorMessage = '';
    this.errors = {};
    this.successMessage = '';

    if (!this.privateEvent.event_type_id) {
      this.errors = {
        event_type_id: 'This field is required.'
      };
      this.errorMessage = 'Please correct the errors above and try again.';
      this.loadingSave = false;
      return;
    }

    // Success handling is different between creating and updating.
    let eventTypeSubmitPromise: Promise<void>;
    if (this.isNewPrivateEvent) {

      eventTypeSubmitPromise = this.eventTypeService
        .addNonRepeatingEvent(
          this.privateEvent.event_type_id,
          this.privateEvent,
          this.requestBooking !== null ? this.requestBookingId : null
        )
        .then(createdEvent => {
          this.privateEvent = createdEvent;

          // Routing depends on which save button was clicked.
          if (this.submitType === this.SUBMIT_TYPE_GO_BACK) {
            this.router.navigate(
              [this.getBackLink()],
              {
                state: {
                  feedback: {
                    text: this.privateEvent.event_type_name + ' private event added successfully.',
                    status: 'success'
                  }
                }
              }
            );
          } else if (this.submitType === this.SUBMIT_TYPE_CONTINUE) {
            this.router.navigate(
              ['/events/scheduled/private-event/edit/' + this.privateEvent.id.toString() + '/reservations']
            );
          }
        });

    } else {

      eventTypeSubmitPromise = this.eventTypeService
        .updateNonRepeatingEvent(this.privateEvent)
        .then(updatedEvent => {
          this.privateEvent = updatedEvent;

          // Routing depends on which save button was clicked.
          if (this.submitType === this.SUBMIT_TYPE_GO_BACK) {
            this.router.navigate(
              [this.getBackLink()],
              {
                state: {
                  feedback: {
                    text: this.privateEvent.event_type_name + ' private event saved successfully.',
                    status: 'success'
                  }
                }
              }
            );
          } else if (this.submitType === this.SUBMIT_TYPE_CONTINUE) {
            this.router.navigate(
              ['/events/scheduled/private-event/edit/' + this.privateEvent.id.toString() + '/reservations']
            );
          }
        });

    }

    // Error handling is the same for both creating and updating.
    eventTypeSubmitPromise
      .catch(reason => {
        if (reason.status === 400) {
          this.errors = reason.error;
          this.errorMessage = 'Please correct the errors above and try again.';
        } else if (reason.status === 403) {
          if (this.isNewPrivateEvent) {
            this.errorMessage = 'You do not have permission to add private events.';
          } else {
            this.errorMessage = 'You do not have permission to modify this private event.';
          }
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSave = false; });
  }

  public getLocationAreasForLocation(): LocationArea[] {
    if (this.loadingLocationAreas) { return []; }

    return this.locationAreaList.filter(locationArea => locationArea.gym_id === this.privateEvent.location_id);
  }

  /**
   * Get the link back to the private event's details view.
   */
  public getBackLink(): string {
    if (!this.isNewPrivateEvent && this.privateEvent && (this.privateEvent.event_parts.length > 0)) {
      return '/events/scheduled/now/event/' + this.privateEvent.event_parts[0].id.toString();
    } else {
      return '/events/scheduled/now';
    }
  }

  public openQuickAddOccurrencesModal() {
    const modalRef = this.modalService.open(QuickAddOccurrencesModalComponent);

    modalRef.componentInstance.submitCallback = (eventParts: NonRepeatingEventPart[]) => {
      if (this.privateEvent.event_parts.length > 0) {
        const numEventParts = this.privateEvent.event_parts.length;
        if (eventPartIsEmpty(this.privateEvent.event_parts[numEventParts - 1])) {
          this.privateEvent.event_parts.splice(numEventParts - 1, 1);
        }
      }

      this.privateEvent.event_parts = this.privateEvent.event_parts.concat(eventParts);
    }
  }

  public getSelectedEventTypeTaxRate(): string {
    // The template calls this on every change-detection pass, including the
    // ones before the event-type list request has returned.
    if (!this.eventTypeList) { return ''; }
    for (const eventType of this.eventTypeList) {
      if (eventType.id === this.privateEvent.event_type_id) {
        if (eventType.tax_rate_name) {
          let taxRateText = eventType.tax_rate_name;
          if (this.taxRateList) {
            for (const taxRate of this.taxRateList) {
              if (taxRate.id === eventType.tax_rate_id) {
                taxRateText += ' - ' + taxRate.rate_percent + '%';
              }
            }
          }
          return taxRateText;
        }
        else { return 'No Tax Rate'; }
      }
    }

    return 'Event Type Not Found';
  }

  private resetPrivateEvent() {
    this.privateEvent = {
      id: null,
      event_type_id: null,
      event_type_name: '',
      location_id: this.accountService.getMyAccount().default_gym_id,
      location_name: '',
      location_area_id: null,
      capacity: 0,
      rolling_days: 1,
      rolling_days_use_exact_date: false,
      booking_open_time: '00:00',
      pricing_matrix_id: null,
      tax_rate_id: null,
      event_parts: [],
      is_private: true,
      request_form_booking_id: this.requestBookingId,
      private_notes: '',
      occurrence_name: '',
      customer_facing_name: '',
      benefit_membership_id: null,
      benefit_membership_name: '',
      benefit_membership_additional_days: 0,
      created_at: '',
      updated_at: ''
    };

    if (this.requestBooking) {
      this.setInitialEventParametersFromRequestBooking();
    }
  }

  private loadPrivateEvent(eventTypeId: string) {
    this.loadingPrivateEvent = true;

    const eventIdInt = parseInt(eventTypeId, 10);
    if (isNaN(eventIdInt)) {
      this.privateEventNotFound = true;
      this.loadingPrivateEvent = false;
      return;
    }

    this.eventTypeService
      .getNonRepeatingEvent(eventIdInt)
      .then(eventType => {
        this.privateEvent = eventType;

        if (!this.privateEvent.is_private) {
          this.privateEventNotFound = true;
          return;
        }

        this.loadPricingMatrices();

        if (!this.isNewPrivateEvent && this.privateEvent.request_form_booking_id) {
          this.requestBookingId = this.privateEvent.request_form_booking_id;
          this.loadRequestBooking();
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.privateEventNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingPrivateEvent = false; });
  }

  private loadRequestBooking() {
    if (!this.requestBookingId) { return; }

    this.loadingRequestBooking = true;

    this.scheduledBookingService
      .getScheduledBooking(this.requestBookingId)
      .then(scheduledBooking => {
        this.requestBooking = scheduledBooking;
        if (this.isNewPrivateEvent) {
          this.setInitialEventParametersFromRequestBooking();
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.privateEventNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingRequestBooking = false; });
  }

  private loadEventTypes() {
    this.loadingEventTypes = true;

    this.serverError = null;

    this.eventTypeService
      .getEventTypes(
        1,
        3000,
        {
          status: EventTypeStatus.active,
          schedule_type: ScheduleType.oneOff
        }
      )
      .then(response => { this.eventTypeList = response.results; })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventTypes = false; });
  }

  private setInitialEventParametersFromRequestBooking() {
    if (!this.requestBooking) { return; }

    this.privateEvent.location_id = this.requestBooking.location_id;
    this.privateEvent.location_name = this.requestBooking.location_name;
    this.privateEvent.location_area_id = this.requestBooking.location_area_id;
    this.privateEvent.location_area_name = this.requestBooking.location_area_name;
    this.privateEvent.event_parts = this.requestBooking.event_parts
      .map(ep => {
        return {
          start_time: ep.event_start_time,
          start_date: ep.event_date,
          duration_minutes: ep.duration_minutes,
          instructors: ep.instructors.map(inst => {
            return {
              id: inst.id,
              first_name: inst.first_name,
              last_name: inst.last_name,
              is_sub: inst.is_sub
            };
          }),
          start_datetime_isoformat: '',
          end_datetime_isoformat: ''
        };
      });
  }

}
